import { MinusIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { CROPS } from 'constants/farming'
import ModalComponent from 'components/common/ModalComponent'
import SelectComponent from 'components/common/SelectComponent'
import ButtonComponent from 'components/common/ButtonComponent'

const labelClassGroup = 'block text-sm font-medium text-gray-700'
const inputClassGroup = 'w-full p-2 border rounded-md shadow-sm'
const subTitleClassGroup = 'text-l font-semibold text-greenPrimary my-5 pt-4'

const defaultBlockConfigFields = {
  numberOfTunnels: { label: 'Tunnels #', value: '0' },
  numberOfWindows: { label: 'Windows #', value: '16' }
}

const defaultFormData = {
  name: '',
  area: '',
  cropId: '',
  varietyId: '',
  blockGroupId: '',
  useSubBlockBarcode: false,
  isHydroponicBlock: false,
  accessTunnel: 0,
  accessWindow: 1,
  blockConfig: []
}

export default function BlockFormModal ({
  isOpen,
  onClose,
  selectedBlock,
  onSave,
  blockGroups,
  varietiesOptions
}) {
  if (!isOpen) return null

  const isEditBlock = !!selectedBlock?.farmingBlockId

  const getMaxWindow = (formData = selectedBlock) => {
    const configs = formData?.blockConfig || []
    if (configs.length === 0) return 1
    const min = configs.reduce((min, conf) => {
      return Math.min(min, Number(conf.numberOfWindows || 1))
    }, Infinity)
    return min || 1
  }

  const getMaxTunnels = (formData) => {
    if (!Array.isArray(formData.blockConfig)) return 0
    return formData.blockConfig.reduce(
      (sum, conf) => sum + Number(conf.numberOfTunnels || 0),
      0
    )
  }

  const handleSave = (formData) => {
    const payload = {
      name: formData.name,
      area: formData.area,
      cropId: parseInt(formData.cropId),
      varietyId: parseInt(formData.varietyId),
      blockGroupId: Number(formData.blockGroupId),
      useSubBlockBarcode: !!formData.useSubBlockBarcode,
      isHydroponicBlock: !!formData.isHydroponicBlock,
      accessTunnel: Number(formData.accessTunnel || 0),
      accessWindow: Number(formData.accessWindow || 1),
      blockConfig: (formData.blockConfig || []).map((config) => ({
        numberOfTunnels: Number(config.numberOfTunnels),
        numberOfWindows: Number(config.numberOfWindows)
      }))
    }

    onSave(payload)
  }

  return (
    <ModalComponent
      title={isEditBlock ? 'Update Farming Block' : 'New Farming Block'}
      isOpen={isOpen}
      onClose={onClose}
      initialData={selectedBlock || {}}
      onSave={handleSave}
    >
      {({ formData: rawFormData, handleChange }) => {
        const formData = { ...defaultFormData, ...rawFormData }

        const selectedCrop = formData?.cropId ? CROPS[parseInt(formData.cropId) - 1] : ''

        const filteredVarieties = Array.isArray(varietiesOptions) ? varietiesOptions.filter(v => v.cropId === parseInt(formData.cropId)) : []

        const handleConfigFieldChange = (e, index) => {
          const { name, value } = e.target
          const updatedConfigs = [...(formData.blockConfig || [])]
          const nextValue = Number(value || 0)
          updatedConfigs[index] = {
            ...updatedConfigs[index],
            [name]: getSafeConfigValue(name, nextValue, index)
          }

          handleChange({
            target: { name: 'blockConfig', value: updatedConfigs }
          })
        }

        const getSafeConfigValue = (field, value, index) => {
          if (field === 'numberOfWindows') {
            return Math.max(Number(formData.accessWindow || 1), value)
          }

          if (field === 'numberOfTunnels') {
            const configs = formData.blockConfig || []
            const otherTunnelTotal = configs.reduce((sum, config, configIndex) => {
              return configIndex === index ? sum : sum + Number(config.numberOfTunnels || 0)
            }, 0)
            const requiredTunnelTotal = Number(formData.accessTunnel || 0)
            return Math.max(0, requiredTunnelTotal - otherTunnelTotal, value)
          }

          return Math.max(0, value)
        }

        const canStepConfigFieldDown = (field, index) => {
          const config = formData.blockConfig?.[index] || {}
          const currentValue = Number(config[field] || 0)
          return getSafeConfigValue(field, currentValue - 1, index) < currentValue
        }

        const stepConfigField = (field, index, amount) => {
          const config = formData.blockConfig?.[index] || {}
          const currentValue = Number(config[field] || 0)
          const updatedConfigs = [...(formData.blockConfig || [])]

          updatedConfigs[index] = {
            ...updatedConfigs[index],
            [field]: getSafeConfigValue(field, currentValue + amount, index)
          }

          handleChange({
            target: { name: 'blockConfig', value: updatedConfigs }
          })
        }

        const addConfigRow = () => {
          const newConfig = Object.fromEntries(
            Object.entries(defaultBlockConfigFields).map(([key, { value }]) => [key, value])
          )

          handleChange({
            target: {
              name: 'blockConfig',
              value: [...(formData.blockConfig || []), newConfig]
            }
          })
        }

        const deleteFarmingBlockConfig = (index) => {
          const updatedConfigs = formData.blockConfig.filter((_, i) => i !== index)
          handleChange({ target: { name: 'blockConfig', value: updatedConfigs } })
        }

        const handleCropChange = (e) => {
          const value = e.target.value
          // const cropName = CROPS[parseInt(value) - 1]

          handleChange({ target: { name: 'cropId', value, type: 'select' } })
          handleChange({ target: { name: 'varietyId', value: '', type: 'select' } })
        }

        const handleVarietyChange = (e) => {
          handleChange({ target: { name: 'varietyId', value: e.target.value } })
        }

        return (
          <div>
            {/* block details section */}
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div>
                <label className={labelClassGroup}>Farming Block Name *</label>
                <input
                  type='text'
                  name='name'
                  value={formData.name}
                  onChange={handleChange}
                  className={inputClassGroup}
                />
              </div>

              <div>
                <label className={labelClassGroup}>Default Crop *</label>
                <SelectComponent
                  name='cropId'
                  value={String(formData.cropId ?? '')}
                  onChange={handleCropChange}
                >
                  <option value='' disabled>Select an Option</option>
                  {CROPS.map((cropName, index) => (
                    <option key={cropName} value={index + 1}>{cropName}</option>
                  ))}
                </SelectComponent>
              </div>

              <div>
                <label className={labelClassGroup}>Default Variety *</label>
                <SelectComponent
                  name='varietyId'
                  value={String(formData.varietyId ?? '')}
                  onChange={handleVarietyChange}
                >
                  <option value='' disabled>Select an Option</option>
                  {selectedCrop && filteredVarieties
                    .map(v => (
                      <option key={v.varietyId} value={v.varietyId}>
                        {v.name}
                      </option>
                    ))}
                </SelectComponent>
              </div>

              <div>
                <label className={labelClassGroup}>Area *</label>
                <input
                  type='text'
                  name='area'
                  value={formData.area}
                  onChange={handleChange}
                  className={inputClassGroup}
                />
              </div>

              <div>
                <label className={labelClassGroup}>Access Tunnel</label>
                <input
                  disabled={isEditBlock}
                  type='number'
                  name='accessTunnel'
                  value={formData.accessTunnel}
                  onChange={handleChange}
                  className={`${isEditBlock ? 'cursor-not-allowed' : ''} ${inputClassGroup}`}
                  min={0}
                  max={getMaxTunnels(formData)}
                />
              </div>
              <div>
                <label className={labelClassGroup}>Access Window</label>
                <input
                  disabled={isEditBlock}
                  type='number'
                  name='accessWindow'
                  value={formData.accessWindow}
                  onChange={handleChange}
                  className={`${isEditBlock ? 'cursor-not-allowed' : ''} ${inputClassGroup}`}
                  min={1}
                  max={getMaxWindow(formData)}
                />
              </div>

              {!!blockGroups.length && (
                <div>
                  <label className={labelClassGroup}>Block Group</label>
                  <SelectComponent
                    name='blockGroupId'
                    value={String(formData.blockGroupId ?? '')}
                    onChange={handleChange}
                  >
                    <option value='' disabled>Select Option</option>
                    {blockGroups.map((bg) => (
                      <option key={bg.blockGroupId} value={bg.blockGroupId}>
                        {bg.groupName}
                      </option>
                    ))}
                  </SelectComponent>
                </div>
              )}

              <div className='mt-4 flex items-center'>
                <input
                  id='useSubBlockBarcode'
                  type='checkbox'
                  name='useSubBlockBarcode'
                  checked={!!formData.useSubBlockBarcode}
                  onChange={handleChange}
                  disabled={isEditBlock || formData.accessWindow === 1}
                  className={`${isEditBlock ? 'cursor-not-allowed' : 'cursor-pointer'} w-5 h-5 border-gray-300 rounded`}
                />
                <label htmlFor='useSubBlockBarcode' className={`${isEditBlock ? 'cursor-not-allowed' : 'cursor-pointer'} ${labelClassGroup} text-xm ml-2`}>
                  Use Sub-Block Barcode
                </label>
              </div>

              <div className='mt-4 flex items-center'>
                <input
                  id='isHydroponicBlock'
                  type='checkbox'
                  name='isHydroponicBlock'
                  checked={!!formData.isHydroponicBlock}
                  onChange={handleChange}
                  disabled={isEditBlock}
                  className={`${isEditBlock ? 'cursor-not-allowed' : 'cursor-pointer'} w-5 h-5 border-gray-300 rounded`}
                />
                <label htmlFor='isHydroponicBlock' className={`${isEditBlock ? 'cursor-not-allowed' : 'cursor-pointer'} ${labelClassGroup} text-xm ml-2`}>
                  Hydroponic Block
                </label>
              </div>
            </div>

            {/* block configuration section */}
            <div>
              <h4 className={subTitleClassGroup}>Farming Block Setup</h4>

              {(formData.blockConfig?.length > 0) && (
                <div className='grid grid-cols-[1fr_1fr_auto] gap-5 border-b pb-2'>
                  {Object.entries(defaultBlockConfigFields).map(([field, { label }]) => (
                    <div key={field}>
                      <label className={labelClassGroup}>{label}</label>
                    </div>
                  ))}
                </div>
              )}

              {formData.blockConfig?.map((config, index) => (
                <div key={index} className='grid grid-cols-[1fr_1fr_auto] gap-5 mt-4 items-center'>
                  {Object.entries(defaultBlockConfigFields).map(([field, { label }]) => (
                    <div key={field}>
                      <div className='flex items-center gap-2'>
                        <button
                          type='button'
                          aria-label={`Remove ${field === 'numberOfTunnels' ? 'tunnel' : 'window'} from setup row ${index + 1}`}
                          title={`Remove ${field === 'numberOfTunnels' ? 'tunnel' : 'window'}`}
                          onClick={() => stepConfigField(field, index, -1)}
                          disabled={!canStepConfigFieldDown(field, index)}
                          className='h-10 w-10 rounded-md border border-gray-200 bg-white text-gray-600 flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-40 hover:bg-gray-50'
                        >
                          <MinusIcon className='w-4 h-4' />
                        </button>
                        <input
                          type='number'
                          name={field}
                          aria-label={`${label} row ${index + 1}`}
                          value={config[field] ?? '0'}
                          onChange={(e) => handleConfigFieldChange(e, index)}
                          className={`${inputClassGroup} text-center`}
                        />
                        <button
                          type='button'
                          aria-label={`Add ${field === 'numberOfTunnels' ? 'tunnel' : 'window'} to setup row ${index + 1}`}
                          title={`Add ${field === 'numberOfTunnels' ? 'tunnel' : 'window'}`}
                          onClick={() => stepConfigField(field, index, 1)}
                          className='h-10 w-10 rounded-md border border-green-100 bg-green-50 text-greenPrimary flex items-center justify-center hover:bg-green-100'
                        >
                          <PlusIcon className='w-4 h-4' />
                        </button>
                      </div>
                    </div>
                  ))}

                  <ButtonComponent
                    type='button'
                    customColorsStyles='bg-white text-red-500 hover:bg-white'
                    onClick={() => deleteFarmingBlockConfig(index)}
                    disabled={isEditBlock && formData.blockConfig.length <= 1}
                    className='w-10 h-10 flex items-center justify-center rounded-md text-red-500'
                  >
                    <TrashIcon className='w-5 h-5' />
                  </ButtonComponent>
                </div>
              ))}
            </div>

            <div className='mt-4'>
              <ButtonComponent type='button' onClick={addConfigRow}>
                <PlusIcon className='w-5 h-5 text-white-500' />
                Add Block Setup Row
              </ButtonComponent>
            </div>
          </div>
        )
      }}
    </ModalComponent>
  )
}
